import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import {
    AnnotationsContextProvider,
    type IAnnotationsContextProviderProps,
} from "app/annotations/AnnotationContextProvider";
import { swSettings } from "app/common/services/swSettings";
import {
    ALL_TOPICS_NAME,
    ANNOTATIONS_TAG_PREFIX,
    getGraphActionAnnotationFetchTagPrefixes,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import type { IAiTrackerCampaign } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import { isTraditionalTrialUser } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

interface ILLMAnnotationsContextProviderProps extends Pick<
    IAnnotationsContextProviderProps,
    "children" | "chartName" | "granularity"
> {
    campaign: IAiTrackerCampaign;
}

export const LLMAnnotationsContextProvider = ({
    children,
    chartName,
    campaign,
    granularity = GranularityNames.DAILY,
}: ILLMAnnotationsContextProviderProps) => {
    const campaignId = campaign?.id;
    const isEnabled = !!campaignId && !campaign.isDemo && !isTraditionalTrialUser(swSettings);
    const {
        params: { topic: queryTopic },
    } = useCurrentStateAndParams();
    
    const isAllTopics = ((queryTopic as string) ?? ALL_TOPICS_NAME) === ALL_TOPICS_NAME;
    const fetchTagPrefixes = useMemo(
        () => [ANNOTATIONS_TAG_PREFIX, ...getGraphActionAnnotationFetchTagPrefixes(isAllTopics)],
        [isAllTopics],
    );

    return (
        <AnnotationsContextProvider
            chartName={chartName}
            enabled={isEnabled}
            annotationId={campaignId}
            tagPrefix={ANNOTATIONS_TAG_PREFIX}
            fetchTagPrefixes={fetchTagPrefixes}
            granularity={granularity}
        >
            {children}
        </AnnotationsContextProvider>
    );
};
